/**
 * ── CSV SERIALISATION — the one place a cell is escaped and a file is framed ─────────
 *
 * Extracted from `utils/exportActivity.ts` (VERA-260), which had the only escape function
 * in the platform until the analytics export needed a second one. §36's rule is that one
 * hand-roll is allowed and the SECOND is a shared module, so this is that module rather
 * than a copy of `escapeCsvValue` with a different name.
 *
 * ## Why the BOM is not optional
 *
 * Excel on Windows and Excel for Mac both decode a `.csv` with no byte-order mark using
 * the SYSTEM legacy codepage, not UTF-8. The scan map carries `geo_city` values straight
 * out of reverse geocoding — 東京, 上海, Zürich, São Paulo — and every one of them renders
 * as mojibake in that path. A leading `﻿` is the documented signal that makes Excel
 * read the file as UTF-8, and it is invisible to every other consumer this repo has
 * (`pandas.read_csv` strips it under `utf-8-sig`, Numbers and Sheets both ignore it).
 *
 * It is applied by `toCsvFile` rather than left to each caller for the same reason the
 * area filter is merged inside the analytics hooks (§42's 2-of-37 finding): a per-caller
 * opt-in is a per-caller thing to forget, and the failure is silent — the file opens, it
 * just has the wrong characters in it.
 *
 * ## NUMBERS ARE NEVER LOCALE-FORMATTED HERE
 *
 * A cell containing `1,234.00` is two columns in a comma-delimited file unless it is
 * quoted, and even quoted it imports as TEXT rather than as a number in every spreadsheet
 * that reads it. Grouping separators are a rendering decision that belongs to the screen.
 * Callers pass `String(n)` (or a fixed-precision form); this module never formats.
 */

/**
 * Escape one cell.
 *
 * Quotes only when it must — a comma, a quote or a newline in the value. Unconditional
 * quoting would also be valid CSV, but it makes a hand-read file noisier and it is not
 * what the activity export has emitted since it shipped, so the behaviour is preserved
 * exactly rather than "improved" in a shared module nobody asked to change.
 *
 * A leading/trailing space is NOT a quoting trigger. RFC 4180 leaves it undefined and
 * every reader this repo cares about preserves it either way.
 */
export const escapeCsvCell = (value: string): string =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

/** One row of already-stringified cells → one CSV line. */
export const toCsvRow = (cells: readonly string[]): string =>
  cells.map(escapeCsvCell).join(',')

/**
 * Rows → CSV body, CRLF-delimited.
 *
 * CRLF because RFC 4180 specifies it and because Excel's own writer emits it; LF-only
 * files are read correctly by everything modern but round-trip to CRLF the moment anyone
 * saves one from Excel, which shows up as a whole-file diff.
 *
 * ⚠️ NO trailing newline. A trailing CRLF makes several readers (including
 * `csv.reader` with no `skipinitialspace`) yield a final empty row, which on a one-row
 * metric export doubles the apparent row count.
 */
export const toCsvBody = (rows: readonly (readonly string[])[]): string =>
  rows.map(toCsvRow).join('\r\n')

/**
 * The UTF-8 byte-order mark. Exported so a caller assembling bytes directly (the ZIP
 * writer) can prepend it without re-deriving the escape sequence.
 */
export const CSV_BOM = '﻿'

/** Rows → the full file text, BOM included. See the header for why the BOM is mandatory. */
export const toCsvFile = (rows: readonly (readonly string[])[]): string =>
  CSV_BOM + toCsvBody(rows)

/**
 * Hand a Blob to the browser as a download.
 *
 * The temporary-anchor dance is the only mechanism that works across the browsers this
 * app supports; `showSaveFilePicker` is Chromium-only and would need this path as a
 * fallback anyway, so there is no second mechanism to maintain.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
